import { useEffect } from 'react';
import { Flex, Layout } from 'antd';
import { Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import useScrollToTop from 'hooks/useScrollToTop';
import { DashboardSummaryProvider } from 'contexts/DashboardSummaryContext';
import Header from 'components/Header/Header';
import { GlobalToastAlert } from 'components/ToastAlert';
import { PageLoader } from 'components/Spin';
import { selectIsBrandDetailsExist } from 'services/brand/brandSelectors';
import { captureMarketingAttribution } from 'services/marketing/marketingAsync';
import styles from './Layout.module.scss';

function LayoutWithSider() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  useScrollToTop(location.pathname);

  const isBrandDetailsExist = useAppSelector(selectIsBrandDetailsExist);

  /**
   * Marketing attribution only ever appears in the landing URL, so it is read here — the
   * common ancestor of every route — before any in-app navigation can drop the query string.
   * The thunk no-ops once captured, so this stays a read-once (CLAUDE.md rule 8).
   */
  useEffect(() => {
    dispatch(captureMarketingAttribution(window.location.search));
  }, []);

  if (!isBrandDetailsExist) {
    return <PageLoader />;
  }

  // DashboardSummaryProvider must wrap BOTH the Header (whose mobile
  // MenuDrawer renders SidebarMenu → useSidebarMenuItems →
  // useDashboardSummary) AND the Outlet (where the alerts dashboard
  // card lives). Wrapping at the WithSidebar level alone leaves the
  // mobile drawer outside the provider's scope and throws
  // "useDashboardSummary must be used inside <DashboardSummaryProvider>".
  return (
    <DashboardSummaryProvider>
      <Flex gap='middle' wrap='wrap'>
        <Layout className={styles.container}>
          <div className={styles.stickyContainer}>
            <Header />
            <GlobalToastAlert />
          </div>
          <Outlet />
        </Layout>
      </Flex>
    </DashboardSummaryProvider>
  );
}

export default LayoutWithSider;
